import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { completeNewPasswordChallenge, getCognitoErrorMessage, retryWithBackoff, isTransientCognitoError, isDeviceKeyError } from '../utils/authHelpers';
import { preAuthPost } from '../utils/preAuthApi';
import { TrustedDevice, DeviceTrustSettings, CognitoDevice } from '../types/device-trust';
import { getCognitoUserPool, isLocalDev, WindowWithConfig } from '../utils/cognitoPool';
import { logger } from '../utils/logger';
import { extractApiErrorMessage } from '../utils/errorUtils';
import { withTimeout, TimeoutError, AUTH_TIMEOUT_MS, SESSION_TIMEOUT_MS, TOKEN_REFRESH_TIMEOUT_MS } from '../utils/withTimeout';
import { clearCognitoDeviceKeys } from '../utils/cognitoDeviceKeys';

interface User {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  subscriptionPlan?: string;
  businessName?: string;
  businessType?: string;
  creatorId?: string;
  permissions?: string[];
}

interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'basic' | 'catalog_owner' | 'commercial';
  businessName?: string;
  businessType?: string;
  subscriptionId?: string;
  tier?: string;
  aliIdPrefix?: string;
}

interface ChallengeData {
  cognitoUser: CognitoUser;
  userAttributes?: Record<string, string>;
  requiredAttributes?: string[];
}

// Extended Error types for authentication challenges
interface AuthError extends Error {
  requireMFASetup?: boolean;
  cognitoUser?: CognitoUser;
  session?: CognitoUserSession;
  challengeData?: ChallengeData;
  mfaSetupData?: {
    cognitoUser: CognitoUser;
    challengeName: string;
    challengeParameters: Record<string, string>;
  };
  mfaData?: {
    cognitoUser: CognitoUser;
    challengeName: string;
    challengeParameters: Record<string, string>;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  loginWithTokens: (tokens: { idToken: string; accessToken: string; refreshToken: string }) => Promise<void>;
  signUp: (userData: SignUpData) => Promise<void>;
  completeSignupAfterPayment: (sessionId: string) => Promise<void>;
  changePassword: (username: string, newPassword: string, session?: ChallengeData) => Promise<void>;
  changeUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  setupMFA: () => Promise<string>;
  verifyMFA: (code: string) => Promise<void>;
  submitMFACode: (cognitoUser: CognitoUser, code: string, challengeName?: string) => Promise<void>;
  // Cognito device tracking (device key registration)
  rememberDevice: () => Promise<void>;
  forgetDevice: () => Promise<void>;
  getDeviceStatus: () => Promise<string | null>;
  // API-based device trust for MFA skip (#740)
  trustCurrentDevice: (deviceName: string, bindIp?: boolean) => Promise<void>;
  getTrustedDevices: () => Promise<TrustedDevice[]>;
  getCognitoDevices: () => Promise<CognitoDevice[]>;
  revokeTrustedDevice: (deviceId: string) => Promise<void>;
  revokeAllTrustedDevices: () => Promise<number>;
  getDeviceTrustSettings: () => Promise<DeviceTrustSettings>;
  // Get device key for trust operations
  getDeviceKey: () => string | null;
  signOut: () => void;
  error: string | null;
  getIdToken: () => Promise<string | null>;
}

// Export context for use by the hook in hooks/useAuth.ts
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Note: useAuth hook is in hooks/useAuth.ts for fast refresh compatibility




// Type for getUserData response UserAttributes (different from CognitoUserAttribute)
interface UserDataAttribute {
  Name: string;
  Value?: string;
}

export const extractUserFromAttributes = (cognitoUser: CognitoUser, attributes: CognitoUserAttribute[]): User => {
  const email = attributes.find(attr => attr.getName() === 'email')?.getValue() || '';
  const firstName = attributes.find(attr => attr.getName() === 'given_name')?.getValue();
  const lastName = attributes.find(attr => attr.getName() === 'family_name')?.getValue();

  const role = attributes.find(attr => attr.getName() === 'custom:userRole')?.getValue();
  const subscriptionPlan = attributes.find(attr => attr.getName() === 'custom:subscriptionPlan')?.getValue();
  const businessName = attributes.find(attr => attr.getName() === 'custom:businessName')?.getValue();
  const businessType = attributes.find(attr => attr.getName() === 'custom:businessType')?.getValue();
  const creatorId = attributes.find(attr => attr.getName() === 'custom:creatorId')?.getValue();
  const permissions = attributes.find(attr => attr.getName() === 'custom:permissions')?.getValue()?.split(',') || [];

  return {
    username: email || cognitoUser.getUsername(),
    email,
    firstName,
    lastName,
    role,
    subscriptionPlan,
    businessName,
    businessType,
    creatorId,
    permissions,
  };
};

// Extract user from getUserData response format (Name/Value objects instead of getName()/getValue() methods)
export const extractUserFromUserData = (cognitoUser: CognitoUser, attributes: UserDataAttribute[]): User => {
  const findAttr = (name: string) => attributes.find(attr => attr.Name === name)?.Value;

  const email = findAttr('email') || '';
  return {
    username: email || cognitoUser.getUsername(),
    email,
    firstName: findAttr('given_name'),
    lastName: findAttr('family_name'),
    role: findAttr('custom:userRole'),
    subscriptionPlan: findAttr('custom:subscriptionPlan'),
    businessName: findAttr('custom:businessName'),
    businessType: findAttr('custom:businessType'),
    creatorId: findAttr('custom:creatorId'),
    permissions: findAttr('custom:permissions')?.split(',') || [],
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userPool, setUserPool] = useState<CognitoUserPool | null>(null);
  const [currentUser, setCurrentUser] = useState<CognitoUser | null>(null);

  const checkAuthState = async () => {
    try {
      // For local development, check for stored tokens
      if (isLocalDev()) {
        logger.info('Local dev mode: checking for stored tokens');
        const localToken = localStorage.getItem('localDevIdToken');
        if (localToken) {
          // Try to decode the token to get user info
          try {
            // JWT uses URL-safe base64, convert to standard base64
            const base64Payload = localToken.split('.')[1]
              .replace(/-/g, '+')
              .replace(/_/g, '/');
            const payload = JSON.parse(atob(base64Payload));
            logger.info('Local dev: decoded token payload', payload);
            setUser({
              username: payload.email || payload['cognito:username'],
              email: payload.email,
              firstName: payload.given_name,
              lastName: payload.family_name,
            });
            logger.info('Local dev: user restored from token');
          } catch (decodeError) {
            logger.info('Failed to decode local dev token', decodeError);
            localStorage.removeItem('localDevIdToken');
            localStorage.removeItem('localDevAccessToken');
            localStorage.removeItem('localDevRefreshToken');
          }
        } else {
          logger.info('Local dev: no stored token found');
        }
        setIsLoading(false);
        return;
      }

      const pool = getCognitoUserPool();
      setUserPool(pool);

      const cognitoUser = pool.getCurrentUser();
      if (cognitoUser) {
        setCurrentUser(cognitoUser);

        const sessionPromise = new Promise<void>((resolve) => {
          cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
            if (err || !session) {
              setUser(null);
              setCurrentUser(null);
              setIsLoading(false);
              resolve();
              return;
            }

            if (!session.isValid()) {
              cognitoUser.refreshSession(session.getRefreshToken(), (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
                if (refreshErr || !newSession?.isValid()) {
                  setUser(null);
                  setCurrentUser(null);
                  setIsLoading(false);
                  resolve();
                  return;
                }

                cognitoUser.getUserAttributes((attrErr, attributes) => {
                  if (!attrErr && attributes) {
                    setUser(extractUserFromAttributes(cognitoUser, attributes));
                  }
                  setIsLoading(false);
                  resolve();
                });
              });
              return;
            }

            cognitoUser.getUserAttributes((attrErr, attributes) => {
              if (!attrErr && attributes) {
                setUser(extractUserFromAttributes(cognitoUser, attributes));
              }
              setIsLoading(false);
              resolve();
            });
          });
        });

        try {
          return await withTimeout(sessionPromise, SESSION_TIMEOUT_MS, 'Session restoration');
        } catch (err) {
          if (err instanceof TimeoutError) {
            logger.warn('Session restoration timed out — clearing stale device keys');
            clearCognitoDeviceKeys(pool.getClientId(), cognitoUser.getUsername());
          }
          setUser(null);
          setCurrentUser(null);
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      logger.error('Failed to initialize Cognito', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuthState();
  }, []);

  const signOut = useCallback(() => {
    // Clear local dev tokens
    if (isLocalDev()) {
      localStorage.removeItem('localDevIdToken');
      localStorage.removeItem('localDevAccessToken');
      localStorage.removeItem('localDevRefreshToken');
    }

    if (currentUser) {
      currentUser.signOut();
    }

    localStorage.setItem('cognito-logout', 'true');
    setTimeout(() => localStorage.removeItem('cognito-logout'), 1000);

    setUser(null);
    setCurrentUser(null);
    setError(null);
  }, [currentUser]);



  const signIn = async (username: string, password: string) => {
    // Ensure user pool is initialized
    if (!userPool) {
      try {
        const pool = getCognitoUserPool();
        setUserPool(pool);

        // Use the newly created pool for this sign-in
        return await performSignIn(pool, username, password);
      } catch (error) {
        logger.error('Failed to initialize user pool for sign-in', error);
        throw new Error('User pool not initialized');
      }
    }

    return await performSignIn(userPool, username, password);
  };

  const performSignIn = async (pool: CognitoUserPool, username: string, password: string) => {
    setError(null);
    setIsLoading(true);

    // For local development, use the backend auth endpoint instead of Cognito SDK
    const localDevMode = isLocalDev();
    const windowWithConfig = window as WindowWithConfig;
    logger.info('performSignIn: isLocalDev =', localDevMode, 'ENV =', windowWithConfig.APP_CONFIG?.ENV || import.meta.env.VITE_ENV);

    if (localDevMode) {
      try {
        logger.info('Local dev mode: using backend auth endpoint via preAuthApi');

        // Use pre-auth API utility (no token injection)
        const result = await preAuthPost('/api/auth/login', {
          email: username,
          password
        });

        if (result.error) {
          setError(result.error || 'Authentication failed');
          setIsLoading(false);
          throw new Error(result.error || 'Authentication failed');
        }

        // Store tokens in localStorage for local dev
        if (result.idToken) {
          logger.info('Local dev: storing tokens in localStorage');
          localStorage.setItem('localDevIdToken', result.idToken);
          localStorage.setItem('localDevAccessToken', result.accessToken);
          localStorage.setItem('localDevRefreshToken', result.refreshToken);
        }

        // Set user from response
        const userData = {
          username: result.user.username,
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
        };
        logger.info('Local dev: setting user', userData);
        setUser(userData);

        setIsLoading(false);
        logger.info('Local dev: login complete');
        return;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
        setError(errorMessage);
        setIsLoading(false);
        throw error;
      }
    }

    // Check if username is an email and lookup the actual Cognito username
    let actualUsername = username;
    if (username.includes('@')) {
      try {
        // Use pre-auth API utility (no token injection)
        const lookupResult = await preAuthPost('/api/auth/lookup-username', {
          email: username
        });

        if (lookupResult.found && lookupResult.username) {
          actualUsername = lookupResult.username;
        }
      } catch (error) {
        // Lookup failed, proceed with email as username
        logger.debug('Username lookup failed, proceeding with email', error);
      }
    }

    // Attempt authentication with timeout and single retry on device key errors
    const attemptAuthentication = (isRetry: boolean): Promise<void> => {
      const authPromise = new Promise<void>((resolve, reject) => {
        const cognitoUser = new CognitoUser({
          Username: actualUsername,
          Pool: pool,
        });

        const authenticationDetails = new AuthenticationDetails({
          Username: actualUsername,
          Password: password,
        });

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (session: CognitoUserSession) => {
            // IMPORTANT: Do NOT call setCurrentUser here - wait until getUserData succeeds
            // This fixes the "invalid code or auth state" error where user appears authenticated
            // but an error is shown because getUserData failed due to Cognito timing issues.

            // Wrap getUserData in a Promise so we can use retryWithBackoff
            const getUserDataPromise = (): Promise<{ UserAttributes: UserDataAttribute[]; UserMFASettingList?: string[] }> => {
              return new Promise((resolveGetData, rejectGetData) => {
                cognitoUser.getUserData((err, userData) => {
                  if (err) {
                    rejectGetData(err);
                  } else if (userData) {
                    resolveGetData(userData as { UserAttributes: UserDataAttribute[]; UserMFASettingList?: string[] });
                  } else {
                    rejectGetData(new Error('No user data returned'));
                  }
                });
              });
            };

            try {
              // Retry getUserData with exponential backoff for transient errors
              const userData = await retryWithBackoff(getUserDataPromise, 3, 200);

              // Authentication complete - set user state
              setCurrentUser(cognitoUser);
              setUser(extractUserFromUserData(cognitoUser, userData.UserAttributes));
              setIsLoading(false);
              resolve();
            } catch (err) {
              // If all retries failed but it was a transient error, try to proceed gracefully
              // by extracting user info from the session token instead of showing an error
              if (isTransientCognitoError(err)) {
                try {
                  // Extract basic user info from the ID token
                  const idToken = session.getIdToken().getJwtToken();
                  const tokenPayload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));

                  // Set current user now that we have fallback data
                  setCurrentUser(cognitoUser);
                  setUser({
                    username: cognitoUser.getUsername(),
                    email: tokenPayload.email || '',
                    firstName: tokenPayload.given_name,
                    lastName: tokenPayload.family_name,
                    role: tokenPayload['custom:userRole'],
                    subscriptionPlan: tokenPayload['custom:subscriptionPlan'],
                    businessName: tokenPayload['custom:businessName'],
                    businessType: tokenPayload['custom:businessType'],
                    creatorId: tokenPayload['custom:creatorId'],
                    permissions: tokenPayload['custom:permissions']?.split(',') || [],
                  });
                  setIsLoading(false);
                  resolve();
                  return;
                } catch (fallbackErr) {
                  logger.error('Failed to extract user from token', fallbackErr);
                }
              }

              // Non-transient error or fallback failed - show error to user
              const friendlyMessage = getCognitoErrorMessage(err as { code?: string; name?: string; message?: string });
              setError(friendlyMessage);
              setIsLoading(false);
              reject(err);
            }
          },
          onFailure: (err) => {
            const friendlyMessage = getCognitoErrorMessage(err);
            setError(friendlyMessage);
            setIsLoading(false);
            reject(err);
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            setIsLoading(false);
            const error = new Error('Password change required') as AuthError;
            error.challengeData = {
              cognitoUser,
              userAttributes,
              requiredAttributes,
            };
            reject(error);
          },
          mfaSetup: (challengeName, challengeParameters) => {
            setIsLoading(false);
            const error = new Error('MFA setup required') as AuthError;
            error.mfaSetupData = {
              cognitoUser,
              challengeName,
              challengeParameters,
            };
            reject(error);
          },
          totpRequired: (challengeName, challengeParameters) => {
            setIsLoading(false);
            const error = new Error('MFA code required') as AuthError;
            error.mfaData = {
              cognitoUser,
              challengeName,
              challengeParameters,
            };
            reject(error);
          },
          selectMFAType: (challengeName, challengeParameters) => {
            setIsLoading(false);
            const error = new Error('MFA code required') as AuthError;
            error.mfaData = {
              cognitoUser,
              challengeName,
              challengeParameters,
            };
            reject(error);
          },
          mfaRequired: (challengeName, challengeParameters) => {
            setIsLoading(false);
            const error = new Error('MFA code required') as AuthError;
            error.mfaData = {
              cognitoUser,
              challengeName,
              challengeParameters,
            };
            reject(error);
          },
      });
    });

      return withTimeout(authPromise, AUTH_TIMEOUT_MS, 'Authentication');
    };

    try {
      return await attemptAuthentication(false);
    } catch (err) {
      // On timeout or device key error, clear stale device keys and retry once
      if ((err instanceof TimeoutError || isDeviceKeyError(err)) ) {
        logger.warn('Authentication failed due to timeout or device key error — retrying with clean device state');
        clearCognitoDeviceKeys(pool.getClientId(), actualUsername);
        setError(null);
        setIsLoading(true);
        return await attemptAuthentication(true);
      }
      throw err;
    }
  };

  const loginWithTokens = async (tokens: { idToken: string; accessToken: string; refreshToken: string }) => {
    setError(null);
    setIsLoading(true);

    try {
      // Decode idToken to extract user info
      const base64Payload = tokens.idToken.split('.')[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const payload = JSON.parse(atob(base64Payload));

      if (isLocalDev()) {
        // Local dev: store tokens in localStorage (same as signIn)
        localStorage.setItem('localDevIdToken', tokens.idToken);
        localStorage.setItem('localDevAccessToken', tokens.accessToken);
        localStorage.setItem('localDevRefreshToken', tokens.refreshToken);
      } else {
        // Production: cache tokens in Cognito SDK's localStorage format
        // so that pool.getCurrentUser() and getSession() find them on next load
        const pool = userPool || getCognitoUserPool();
        const clientId = pool.getClientId();
        const username = payload['cognito:username'] || payload.sub;

        const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
        localStorage.setItem(`${keyPrefix}.${username}.idToken`, tokens.idToken);
        localStorage.setItem(`${keyPrefix}.${username}.accessToken`, tokens.accessToken);
        localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, tokens.refreshToken);
        localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);

        // Create CognitoUser for this session
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: pool,
        });
        setCurrentUser(cognitoUser);
      }

      // Set user state from decoded token
      setUser({
        username: payload.email || payload['cognito:username'] || payload.sub,
        email: payload.email || '',
        firstName: payload.given_name,
        lastName: payload.family_name,
        role: payload['custom:userRole'],
        subscriptionPlan: payload['custom:subscriptionPlan'],
        businessName: payload['custom:businessName'],
        businessType: payload['custom:businessType'],
        creatorId: payload['custom:creatorId'],
        permissions: payload['custom:permissions']?.split(',') || [],
      });

      setIsLoading(false);
    } catch (error) {
      logger.error('loginWithTokens failed', error);
      setIsLoading(false);
      throw error;
    }
  };

  const signUp = async (userData: SignUpData) => {
    setError(null);
    setIsLoading(true);

    try {
      // Use pre-auth API utility (no token injection)
      const result = await preAuthPost('/register', userData);

      if (result.error) {
        throw new Error(result.error || 'Registration failed');
      }

      setIsLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setError(errorMessage);
      setIsLoading(false);
      throw error;
    }
  };

  const completeSignupAfterPayment = async (sessionId: string) => {
    setError(null);
    setIsLoading(true);

    try {
      // Get pending signup data from session storage
      const pendingSignupData = sessionStorage.getItem('pendingSignup');
      if (!pendingSignupData) {
        throw new Error('No pending signup data found');
      }

      const signupData = JSON.parse(pendingSignupData);

      // Add subscription info to signup data
      signupData.sessionId = sessionId;

      // Complete the signup
      await signUp(signupData);

      // Clear pending signup data
      sessionStorage.removeItem('pendingSignup');

      setIsLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete signup';
      setError(errorMessage);
      setIsLoading(false);
      throw error;
    }
  };

  const changePassword = async (username: string, newPassword: string, challengeData?: ChallengeData) => {
    setError(null);
    setIsLoading(true);

    try {
      if (challengeData?.cognitoUser) {
        // Use the helper function to handle NEW_PASSWORD_REQUIRED challenge
        await completeNewPasswordChallenge(
          challengeData.cognitoUser,
          newPassword,
          challengeData.userAttributes,
          challengeData.requiredAttributes
        );

        setCurrentUser(challengeData.cognitoUser);

        // Get user attributes after successful password change
        return new Promise<void>((resolve) => {
          challengeData.cognitoUser.getUserAttributes((err: Error | null, attributes?: CognitoUserAttribute[]) => {
            if (!err && attributes) {
              const email = attributes.find((attr: CognitoUserAttribute) => attr.getName() === 'email')?.getValue() || '';
              setUser({
                username: email || challengeData.cognitoUser.getUsername(),
                email,
              });
            }
            setIsLoading(false);
            resolve();
          });
        });
      } else {
        throw new Error('Invalid challenge data');
      }
    } catch (err) {
      logger.error('Password change error', err);
      const friendlyMessage = getCognitoErrorMessage(err as Error);
      setError(friendlyMessage);
      setIsLoading(false);
      throw err;
    }
  };

  const changeUserPassword = async (currentPassword: string, newPassword: string) => {
    if (!currentUser) throw new Error('No authenticated user');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      currentUser.changePassword(currentPassword, newPassword, (err) => {
        setIsLoading(false);
        if (err) {
          setError(getCognitoErrorMessage(err));
          reject(err);
        } else {
          resolve();
        }
      });
    });
  };

  const forgotPassword = async (username: string) => {
    if (!userPool) throw new Error('User pool not initialized');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          setIsLoading(false);
          resolve();
        },
        onFailure: (err) => {
          setError(getCognitoErrorMessage(err));
          setIsLoading(false);
          reject(err);
        },
      });
    });
  };

  const confirmPassword = async (username: string, code: string, newPassword: string) => {
    if (!userPool) throw new Error('User pool not initialized');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          setIsLoading(false);
          resolve();
        },
        onFailure: (err) => {
          setError(getCognitoErrorMessage(err));
          setIsLoading(false);
          reject(err);
        },
      });
    });
  };

  const setupMFA = async (): Promise<string> => {
    if (!currentUser) throw new Error('No authenticated user');

    setError(null);
    setIsLoading(true);

    return new Promise<string>((resolve, reject) => {
      currentUser.associateSoftwareToken({
        onSuccess: (session) => {
          setIsLoading(false);
          resolve(session);
        },
        onFailure: (err) => {
          setError(getCognitoErrorMessage(err));
          setIsLoading(false);
          reject(err);
        },
        associateSecretCode: (secretCode) => {
          setIsLoading(false);
          resolve(secretCode);
        },
      });
    });
  };

  const verifyMFA = async (code: string): Promise<void> => {
    if (!currentUser) throw new Error('No authenticated user');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      currentUser.verifySoftwareToken(code, 'My TOTP device', {
        onSuccess: () => {
          setIsLoading(false);
          resolve();
        },
        onFailure: (err) => {
          setError(getCognitoErrorMessage(err));
          setIsLoading(false);
          reject(err);
        },
      });
    });
  };

  const submitMFACode = async (cognitoUser: CognitoUser, code: string, challengeName?: string): Promise<void> => {
    setError(null);
    setIsLoading(true);

    // Use provided challenge name or default to SOFTWARE_TOKEN_MFA
    const mfaType = challengeName || 'SOFTWARE_TOKEN_MFA';
    return new Promise<void>((resolve, reject) => {
      cognitoUser.sendMFACode(code, {
        onSuccess: () => {
          setCurrentUser(cognitoUser);
          cognitoUser.getUserAttributes((err: Error | null, attributes?: CognitoUserAttribute[]) => {
            if (!err && attributes) {
              setUser(extractUserFromAttributes(cognitoUser, attributes));
            }
            setIsLoading(false);
            resolve();
          });
        },
        onFailure: (err: Error) => {
          logger.error('MFA verification failed', err);
          setError(getCognitoErrorMessage(err));
          setIsLoading(false);
          reject(err);
        },
      }, mfaType);
    });
  };

  const getIdToken = (): Promise<string | null> => {
    // For local development, return the stored token
    if (isLocalDev()) {
      const localToken = localStorage.getItem('localDevIdToken');
      return Promise.resolve(localToken);
    }

    if (!currentUser) return Promise.resolve(null);

    const tokenPromise = new Promise<string | null>((resolve) => {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        if (!session.isValid()) {
          currentUser.refreshSession(session.getRefreshToken(), (refreshErr: Error | null, newSession: CognitoUserSession | null) => {
            if (refreshErr || !newSession?.isValid()) {
              resolve(null);
              return;
            }
            resolve(newSession.getIdToken().getJwtToken());
          });
          return;
        }

        resolve(session.getIdToken().getJwtToken());
      });
    });

    return withTimeout(tokenPromise, TOKEN_REFRESH_TIMEOUT_MS, 'Token refresh').catch((err) => {
      if (err instanceof TimeoutError) {
        logger.warn('Token refresh timed out');
        return null;
      }
      return null;
    });
  };

  const rememberDevice = async (): Promise<void> => {
    if (!currentUser) throw new Error('No authenticated user');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      // First check if device key exists
      const deviceKey = currentUser.deviceKey;
      if (!deviceKey) {
        setError('Device not registered. Please sign out and sign in again to enable device tracking.');
        setIsLoading(false);
        reject(new Error('Device not registered'));
        return;
      }

      currentUser.setDeviceStatusRemembered({
        onSuccess: () => {
          setIsLoading(false);
          resolve();
        },
        onFailure: (err) => {
          logger.error('Failed to remember device', err);
          setError('Failed to remember device. Try signing out and back in.');
          setIsLoading(false);
          reject(err);
        },
      });
    });
  };

  const forgetDevice = async (): Promise<void> => {
    if (!currentUser) throw new Error('No authenticated user');

    setError(null);
    setIsLoading(true);

    return new Promise<void>((resolve, reject) => {
      const deviceKey = currentUser.deviceKey;
      if (!deviceKey) {
        setError('No device to forget');
        setIsLoading(false);
        reject(new Error('No device key'));
        return;
      }

      currentUser.forgetDevice({
        onSuccess: () => {
          setIsLoading(false);
          resolve();
        },
        onFailure: (err) => {
          logger.error('Failed to forget device', err);
          setError('Failed to forget device');
          setIsLoading(false);
          reject(err);
        },
      });
    });
  };

  const getDeviceStatus = async (): Promise<string | null> => {
    if (!currentUser) return null;

    return new Promise<string | null>((resolve) => {
      const deviceKey = currentUser.deviceKey;

      if (!deviceKey) {
        resolve('not_registered');
        return;
      }

      // Check if device is remembered by attempting to get device status
      currentUser.getDevice({
        onSuccess: (device) => {
          const status = device.DeviceAttributes?.find(attr => attr.Name === 'device_status')?.Value;
          resolve(status || 'unknown');
        },
        onFailure: (err) => {
          resolve('unknown');
        },
      });
    });
  };

  // Get device key for trust operations
  const getDeviceKey = (): string | null => {
    if (!currentUser) return null;
    return currentUser.deviceKey || null;
  };

  // Helper function to get API URL
  const getApiUrl = (): string => {
    const windowWithConfig = window as WindowWithConfig;
    return (
      windowWithConfig.APP_CONFIG?.API_URL ||
      import.meta.env.VITE_API_URL ||
      'https://api-a.on.artlegacyinstitute.org'
    );
  };

  // API-based device trust methods (#740)
  // These use our backend API, not Cognito's device tracking

  const trustCurrentDevice = async (deviceName: string, bindIp: boolean = true): Promise<void> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const deviceKey = getDeviceKey();
    if (!deviceKey) {
      throw new Error('Device not registered. Please sign out and sign in again.');
    }

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/trust`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Device-Key': deviceKey,
      },
      body: JSON.stringify({
        device_name: deviceName,
        bind_ip: bindIp,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(data, response.status) || 'Failed to trust device');
    }
  };

  const getTrustedDevices = async (): Promise<TrustedDevice[]> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/trusted`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get trusted devices');
    }

    const data = await response.json();
    // Map snake_case to camelCase
    return data.map((d: Record<string, unknown>) => ({
      id: d.id as string,
      deviceName: d.device_name as string,
      trustedAt: d.trusted_at as string,
      expiresAt: d.expires_at as string,
      lastUsedAt: d.last_used_at as string | undefined,
      lastMfaVerifiedAt: d.last_mfa_verified_at as string | undefined,
      trustedIpRange: d.trusted_ip_range as string | undefined,
    }));
  };

  const getCognitoDevices = async (): Promise<CognitoDevice[]> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/cognito-devices`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Return empty array on error instead of throwing
      // This ensures the UI still works even if Cognito device listing fails
      return [];
    }

    const data = await response.json();
    // Map snake_case to camelCase
    return data.map((d: Record<string, unknown>) => ({
      deviceKey: d.device_key as string,
      deviceName: d.device_name as string,
      createdAt: d.created_at as string | undefined,
      lastModifiedAt: d.last_modified_at as string | undefined,
      lastAuthenticatedAt: d.last_authenticated_at as string | undefined,
    }));
  };

  const revokeTrustedDevice = async (deviceId: string): Promise<void> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/trust/${deviceId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(data, response.status) || 'Failed to revoke device');
    }
  };

  const revokeAllTrustedDevices = async (): Promise<number> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/trust`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to revoke all devices');
    }

    const data = await response.json();
    return data.devices_revoked || 0;
  };

  const getDeviceTrustSettings = async (): Promise<DeviceTrustSettings> => {
    const token = await getIdToken();
    if (!token) throw new Error('Not authenticated');

    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/devices/settings`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get device trust settings');
    }

    const data = await response.json();
    return {
      trustDurationDays: data.trust_duration_days,
      maxDevices: data.max_devices,
      mfaReverificationDays: data.mfa_reverification_days,
      deviceCount: data.device_count,
    };
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      signIn,
      loginWithTokens,
      signUp,
      completeSignupAfterPayment,
      changePassword,
      changeUserPassword,
      forgotPassword,
      confirmPassword,
      setupMFA,
      verifyMFA,
      submitMFACode,
      // Cognito device tracking
      rememberDevice,
      forgetDevice,
      getDeviceStatus,
      // API-based device trust (#740)
      trustCurrentDevice,
      getTrustedDevices,
      getCognitoDevices,
      revokeTrustedDevice,
      revokeAllTrustedDevices,
      getDeviceTrustSettings,
      getDeviceKey,
      signOut,
      error,
      getIdToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
